/**
 * TableSkeleton — placeholder de carga para tablas grandes.
 *
 * Reemplaza al `<Loader2 spinner /> + 'Cargando...'` (paquete Y).
 * Estética: shimmer azul muy sutil (rgba(0, 48, 87, ~10%)) — definido en
 * globals.css como `.skeleton`. Respeta `prefers-reduced-motion`.
 *
 * Uso típico:
 *   if (isLoading) return <TableSkeleton rows={8} columns={6} />;
 */

interface TableSkeletonProps {
  /** Número de filas a renderizar (default 6). */
  rows?: number;
  /** Número de columnas (default 5). */
  columns?: number;
  /** Mostrar header skeleton (default true). */
  withHeader?: boolean;
  /** Texto bajo el skeleton (default "Cargando..."). null para ocultar. */
  caption?: string | null;
}

export function TableSkeleton({
  rows = 6,
  columns = 5,
  withHeader = true,
  caption = 'Cargando...',
}: TableSkeletonProps) {
  return (
    <div
      className="overflow-hidden rounded border border-gray-100"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="divide-y divide-gray-100">
        {withHeader && (
          <div className="grid gap-2 bg-gray-50 p-3" style={gridTemplate(columns)}>
            {Array.from({ length: columns }).map((_, i) => (
              <div key={i} className="skeleton h-3" />
            ))}
          </div>
        )}
        {Array.from({ length: rows }).map((_, rowI) => (
          <div key={rowI} className="grid gap-2 p-3" style={gridTemplate(columns)}>
            {Array.from({ length: columns }).map((_, colI) => (
              <div
                key={colI}
                className="skeleton h-3.5"
                style={{
                  // Variación leve de ancho para que no se vea tan plano
                  width: `${70 + ((rowI + colI) % 4) * 8}%`,
                }}
              />
            ))}
          </div>
        ))}
      </div>
      {caption && (
        <div className="border-t border-gray-100 bg-gray-50 px-3 py-2 text-[11px] text-gray-600">
          {caption}
        </div>
      )}
    </div>
  );
}

function gridTemplate(cols: number): React.CSSProperties {
  return { gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` };
}
